import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { Link, useParams, useSearchParams } from 'react-router-dom'
import {
  downloadAllFullMedia,
  getApiErrorMessage,
  getModel,
  getModelMedia,
  getModelMediaFolders,
  getModelProviders,
  getTimezones,
  getPhotoSession,
  getPhotoSessions,
  createPhotoSession,
  updatePhotoSession,
  createMediaGroup,
  updateMediaGroup,
  moveMediaToSession,
  addMediaToGroup,
  removeMediaFromPhotoSession,
  reorderGroupMedia,
  reorderMediaGroups,
  reorderPhotoSessionMedia,
  syncModelMedia,
  updateModelAccountDescription,
} from '../../api'
import { listConfigs } from '../../api/graphConfigs'
import useModelMedia from '../../hooks/useModelMedia'
import MediaEditModal from '../../components/MediaEditModal'
import MediaThumbnail from '../../components/MediaThumbnail'
import ModelBiblePanel from '../../components/ModelBiblePanel'
import TrafficSegmentationPanel from '../../components/TrafficSegmentationPanel'
import { EmptyState, LoadingBlock, MetricCard, Section, SettingExplainer, StatusPill } from '../../components/v2/V2Primitives'
import { getStoredAuthRole, isAdminRole } from '../../lib/realm'
import { buildModelSetupPayload, MODEL_PROFILE_MAX_LENGTH, profileLength, modelProfileSummary } from '../../lib/modelSetup'
import ModelDetailMediaSection from '../ModelDetail/ModelDetailMediaSection'
import { useWorkspaceScope } from '../../contexts/WorkspaceScopeContext'
import { getMediaPreview } from '../ModelDetail/utils'
import {
  asList,
  folderPayload,
  folderValue,
  formatDate,
  modelTitle,
  providerLabel,
  providerLinks,
  providerStatus,
  providerWebhookDetail,
  providerWebhookStatus,
  selectedFolderLabel,
  setupChecklist,
  statusTone,
  totalOf,
} from './v2Utils'
import AdminV2MediaTile from './AdminV2MediaTile'
import '../../styles/pages/model-detail.css'

const tabs = ['Overview', 'Setup', 'Connections', 'Content', 'Automation', 'Activity']

export default function AdminV2ModelDetail() {
  const { id } = useParams()
  const { capabilities, loading: scopeLoading } = useWorkspaceScope()
  const staff = capabilities?.is_agency_member === true
  const canEditContent = staff && capabilities?.agency_permissions?.includes('content')
  const visibleTabs = staff ? ['Content'] : tabs
  const [searchParams, setSearchParams] = useSearchParams()
  const [loading, setLoading] = useState(true)
  const [loadingMedia, setLoadingMedia] = useState(false)
  const [error, setError] = useState('')
  const [model, setModel] = useState(null)
  const [pauseSaving, setPauseSaving] = useState(false)
  const [providers, setProviders] = useState([])
  const [configs, setConfigs] = useState([])
  const [folders, setFolders] = useState([])
  const [sessions, setSessions] = useState([])
  const [sessionDetails, setSessionDetails] = useState([])
  const [loadingSessionDetails, setLoadingSessionDetails] = useState(false)
  const [sessionDetailsRefreshKey, setSessionDetailsRefreshKey] = useState(0)
  const [folder, setFolder] = useState('')
  const [mediaLimit, setMediaLimit] = useState('120')
  const [showHiddenMedia, setShowHiddenMedia] = useState(false)
  const [media, setMedia] = useState([])
  const [mediaTotal, setMediaTotal] = useState(0)
  const [contentRefreshKey, setContentRefreshKey] = useState(0)
  const requestedTab = tabs.find((tab) => tab.toLowerCase() === String(searchParams.get('tab') || '').toLowerCase())
  const [activeTab, setActiveTab] = useState(requestedTab || 'Overview')
  const [busy, setBusy] = useState('')
  const [actionStatus, setActionStatus] = useState('')
  const [editModalItem, setEditModalItem] = useState(null)
  const [selectedMediaIds, setSelectedMediaIds] = useState([])
  const currentRole = getStoredAuthRole()
  const canManageModelDefaultConfig = isAdminRole(currentRole) || currentRole === 'tester_plus'
  const displayedTab = staff ? 'Content' : activeTab

  function selectTab(tab) {
    setActiveTab(tab)
    const next = new URLSearchParams(searchParams)
    next.set('tab', tab)
    setSearchParams(next, { replace: true })
  }

  useEffect(() => {
    if (requestedTab && requestedTab !== activeTab) setActiveTab(requestedTab)
  }, [requestedTab])

  const onModelUpdate = useCallback((updates) => {
    if (updates?.error !== undefined) setError(updates.error || '')
    const { error: _error, ...rest } = updates || {}
    if (Object.keys(rest).length) {
      setModel((prev) => (prev ? { ...prev, ...rest } : prev))
    }
  }, [])

  const mediaScopeParams = useMemo(() => ({
    limit: Number(mediaLimit) || 120,
    offset: 0,
    include_hidden: showHiddenMedia,
    ...folderPayload(folder),
  }), [folder, mediaLimit, showHiddenMedia])

  const classicMedia = useModelMedia(id, model, onModelUpdate, mediaScopeParams)

  useEffect(() => {
    let alive = true
    async function load() {
      setLoading(true)
      setError('')
      const [modelResult, providersResult, foldersResult, sessionsResult, configsResult] = await Promise.allSettled([
        getModel(id),
        staff ? Promise.resolve([]) : getModelProviders(id),
        getModelMediaFolders(id),
        getPhotoSessions(id),
        canManageModelDefaultConfig ? listConfigs() : Promise.resolve([]),
      ])
      if (!alive) return
      if (modelResult.status === 'fulfilled') setModel(modelResult.value)
      else setError(getApiErrorMessage(modelResult.reason, 'Failed to load model'))
      if (providersResult.status === 'fulfilled') setProviders(asList(providersResult.value))
      if (foldersResult.status === 'fulfilled') setFolders(asList(foldersResult.value))
      if (sessionsResult.status === 'fulfilled') setSessions(asList(sessionsResult.value))
      if (configsResult.status === 'fulfilled') setConfigs(asList(configsResult.value))
      setLoading(false)
    }
    if (!scopeLoading && (!staff || canEditContent)) load()
    return () => { alive = false }
  }, [canManageModelDefaultConfig, id, scopeLoading, staff, canEditContent])

  const reloadSessions = useCallback(() => (
    getPhotoSessions(id)
      .then((result) => setSessions(asList(result)))
      .catch((err) => setError(getApiErrorMessage(err, 'Failed to load photo sessions')))
  ), [id])

  const sessionDetailKey = useMemo(
    () => sessions.map((session) => `${session.id}:${session.updated_at || ''}:${session.media_count || 0}`).join('|'),
    [sessions],
  )

  useEffect(() => {
    let alive = true
    const list = sessions.filter((session) => session?.id)
    if (!list.length) {
      setSessionDetails([])
      setLoadingSessionDetails(false)
      return () => { alive = false }
    }
    setLoadingSessionDetails(true)
    Promise.allSettled(list.map((session) => getPhotoSession(id, session.id)))
      .then((results) => {
        if (!alive) return
        const detailed = results
          .filter((result) => result.status === 'fulfilled')
          .map((result) => result.value)
        setSessionDetails(detailed)
      })
      .catch((err) => { if (alive) setError(getApiErrorMessage(err, 'Failed to load session media')) })
      .finally(() => { if (alive) setLoadingSessionDetails(false) })
    return () => { alive = false }
  }, [id, sessionDetailKey, sessionDetailsRefreshKey, sessions])

  useEffect(() => {
    let alive = true
    setLoadingMedia(true)
    getModelMedia(id, mediaScopeParams)
      .then((result) => {
        if (!alive) return
        const rows = asList(result)
        setMedia(rows)
        setMediaTotal(totalOf(result, rows.length))
      })
      .catch((err) => { if (alive) setError(getApiErrorMessage(err, 'Failed to load model media')) })
      .finally(() => { if (alive) setLoadingMedia(false) })
    return () => { alive = false }
  }, [contentRefreshKey, id, mediaScopeParams])

  const allProviders = providers.length ? providers : providerLinks(model)
  const checklist = useMemo(() => setupChecklist(model, folders), [folders, model])
  const readyCount = checklist.filter((item) => item.done).length
  const scopeLabel = selectedFolderLabel(folders, folder)
  const classicMediaList = asList(classicMedia.media)
  const effectiveMediaTotal = classicMedia.mediaTotal || mediaTotal
  const downloaded = classicMediaList.length
    ? classicMediaList.filter((item) => item.media_path).length
    : media.filter((item) => item.media_path).length

  const refreshMediaFolders = useCallback(async () => {
    try {
      const result = await getModelMediaFolders(id)
      setFolders(asList(result))
    } catch (err) {
      setError(getApiErrorMessage(err, 'Failed to load content sources'))
    }
  }, [id])

  useEffect(() => {
    if (model?.media_sync_status === 'done') refreshMediaFolders()
  }, [model?.media_sync_status, model?.media_sync_updated_at, refreshMediaFolders])

  async function runContentAction(kind) {
    setBusy(kind)
    setActionStatus('')
    try {
      if (kind === 'sync') await syncModelMedia(id, false, folderPayload(folder))
      if (kind === 'download') await downloadAllFullMedia(id, folderPayload(folder))
      setActionStatus(`${kind === 'sync' ? 'Sync' : 'Download'} queued for ${scopeLabel}`)
      if (kind === 'sync') setTimeout(() => setContentRefreshKey((value) => value + 1), 1500)
    } catch (err) {
      setActionStatus(getApiErrorMessage(err, 'Action failed'))
    } finally {
      setBusy('')
    }
  }

  async function toggleBotPause() {
    if (!model) return
    const nextPaused = !readBool(model.bot_paused)
    setPauseSaving(true)
    setError('')
    try {
      const updated = await updateModelAccountDescription(model.id, { bot_paused: nextPaused })
      setModel((prev) => ({ ...prev, ...updated, bot_paused: nextPaused }))
    } catch (err) {
      setError(getApiErrorMessage(err, 'Could not update bot pause state'))
    } finally {
      setPauseSaving(false)
    }
  }

  function updateMediaItem(updated) {
    setMedia((prev) => prev.map((item) => (item.id === updated.id ? { ...item, ...updated } : item)))
  }

  function toggleMediaSelection(mediaId, checked) {
    setSelectedMediaIds((prev) => {
      const idValue = Number(mediaId)
      if (!Number.isFinite(idValue)) return prev
      if (checked) return prev.includes(idValue) ? prev : [...prev, idValue]
      return prev.filter((id) => id !== idValue)
    })
  }

  function clearMediaSelection() {
    setSelectedMediaIds([])
  }

  function refreshContentAfterSessionChange() {
    setContentRefreshKey((value) => value + 1)
    setSessionDetailsRefreshKey((value) => value + 1)
    reloadSessions()
  }

  if (!scopeLoading && staff && !canEditContent) return <EmptyState title="Content access is not assigned" description="Ask your agency owner to enable the content manager permission." />
  if (loading || scopeLoading) return <LoadingBlock label="Loading model workspace..." />
  if (!model) return <EmptyState title="Model not found" description={error || 'Open another model from the V2 list.'} action={<Link to="/v2/models" className="v2-btn v2-btn-primary">Back to models</Link>} />

  return (
    <div className="v2-stack">
      <section className="v2-model-hero">
        <div className="v2-model-identity">
          <div className="v2-avatar v2-avatar-large">{modelTitle(model).slice(0, 1).toUpperCase()}</div>
          <div>
            <p className="v2-kicker">Model #{model.id}</p>
            <h2>{modelTitle(model)}</h2>
            <p>{modelProfileSummary(model.account_description) || 'Profile description is not configured yet.'}</p>
          </div>
        </div>
        <div className="v2-model-hero-actions">
          <StatusPill tone={readBool(model.bot_paused) ? 'bad' : 'good'}>{readBool(model.bot_paused) ? 'Bot paused' : 'Bot live'}</StatusPill>
          <StatusPill tone={readyCount === checklist.length ? 'good' : 'warn'}>{readyCount}/{checklist.length} ready</StatusPill>
          {!staff ? <button type="button" className="v2-btn v2-btn-secondary" disabled={pauseSaving} onClick={toggleBotPause}>
            {pauseSaving ? 'Saving...' : readBool(model.bot_paused) ? 'Resume bot' : 'Pause bot'}
          </button> : null}
        </div>
      </section>

      {error ? <div className="v2-error">{error}</div> : null}

      <div className="v2-tabs">
        {visibleTabs.map((tab) => (
          <button key={tab} type="button" className={displayedTab === tab ? 'is-active' : ''} onClick={() => selectTab(tab)}>
            {tab}
          </button>
        ))}
      </div>

      {displayedTab === 'Overview' ? <OverviewTab model={model} checklist={checklist} allProviders={allProviders} mediaTotal={effectiveMediaTotal} downloaded={downloaded} /> : null}
      {displayedTab === 'Setup' ? (
        <div className="v2-stack">
          <SetupTab
            key={model.id}
            model={model}
            configs={configs}
            canManageModelDefaultConfig={canManageModelDefaultConfig}
            onSaved={(patch) => setModel((prev) => ({ ...prev, ...patch }))}
          />
          <Section eyebrow="Optional memory" title="Model bible" description="Long-form facts are stored separately from the required model profile and are only retrieved when relevant.">
            <ModelBiblePanel modelId={model.id} variant="v2" />
          </Section>
        </div>
      ) : null}
      {displayedTab === 'Connections' ? <ConnectionsTab model={model} providers={allProviders} /> : null}
      {displayedTab === 'Content' ? (
        <ContentTab
          modelId={id}
          model={model}
          folders={folders}
          folder={folder}
          setFolder={setFolder}
          refreshMediaFolders={refreshMediaFolders}
          mediaLimit={mediaLimit}
          setMediaLimit={setMediaLimit}
          showHiddenMedia={showHiddenMedia}
          setShowHiddenMedia={setShowHiddenMedia}
          mediaController={classicMedia}
          allowDownloads={!staff}
          allowAutomationSettings={!staff}
          media={media}
          mediaTotal={mediaTotal}
          sessions={sessions}
          sessionDetails={sessionDetails}
          loadingSessionDetails={loadingSessionDetails}
          loadingMedia={loadingMedia}
          scopeLabel={scopeLabel}
          busy={busy}
          actionStatus={actionStatus}
          runContentAction={runContentAction}
          onEditMedia={setEditModalItem}
          selectedMediaIds={selectedMediaIds}
          onToggleMediaSelection={toggleMediaSelection}
          onClearMediaSelection={clearMediaSelection}
          onSessionChanged={refreshContentAfterSessionChange}
        />
      ) : null}
      {displayedTab === 'Automation' ? <AutomationTab model={model} /> : null}
      {displayedTab === 'Activity' ? <ActivityTab model={model} providers={allProviders} /> : null}

      <MediaEditModal
        open={!!editModalItem}
        modelId={id}
        item={editModalItem}
        onClose={() => setEditModalItem(null)}
        onSaved={updateMediaItem}
      />
    </div>
  )
}

function OverviewTab({ model, checklist, allProviders, mediaTotal, downloaded }) {
  return (
    <div className="v2-two-column">
      <Section eyebrow="Readiness" title="Basic setup checklist" description="Only required operating settings are shown here. Advanced bot internals stay separate.">
        <ul className="v2-readiness-list">
          {checklist.map((item) => (
            <li key={item.key} className={item.done ? 'is-done' : ''}>
              <span>{item.done ? 'OK' : 'TODO'}</span>
              <div>
                <strong>{item.label}</strong>
                <p>{item.help}</p>
              </div>
            </li>
          ))}
        </ul>
      </Section>
      <Section eyebrow="Snapshot" title="Operational context" description="Quick signals before an operator changes anything.">
        <div className="v2-metrics-grid v2-metrics-grid-compact">
          <MetricCard label="Connections" value={allProviders.length} detail="Provider accounts" />
          <MetricCard label="Content" value={mediaTotal} detail="Media in selected scope" />
          <MetricCard label="Downloaded" value={downloaded} detail="Ready local files" tone="good" />
          <MetricCard label="Mode" value={model.automation_mode || 'Not set'} detail="Automation policy" />
          <MetricCard label="Bot" value={readBool(model.bot_paused) ? 'Paused' : 'Live'} detail="Runtime outbound state" tone={readBool(model.bot_paused) ? 'warn' : 'good'} />
        </div>
      </Section>
    </div>
  )
}

function SetupTab({ model, configs, canManageModelDefaultConfig, onSaved }) {
  const [editing, setEditing] = useState(false)
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState('')
  const [timezones, setTimezones] = useState([])
  const [draft, setDraft] = useState({
    description: model.account_description || '',
    timezone: model.timezone || '',
    max_message_price: model.max_message_price == null ? '' : String(model.max_message_price),
    default_config_id: model.default_config_id == null ? '' : String(model.default_config_id),
    automation_mode: model.automation_mode || 'full_auto',
    bot_paused: readBool(model.bot_paused),
    telegram_alerts_enabled: Boolean(model.telegram_alerts_enabled),
  })

  useEffect(() => {
    if (!editing) return undefined
    let cancelled = false
    getTimezones().then((items) => {
      if (!cancelled && Array.isArray(items)) setTimezones(items)
    }).catch(() => {}) // Browser validation remains available; API is authoritative.
    return () => { cancelled = true }
  }, [editing, model.id])

  function updateDraft(key, value) {
    setDraft((prev) => ({ ...prev, [key]: value }))
  }

  async function saveSetup(e) {
    e.preventDefault()
    setSaving(true)
    setError('')
    try {
      const payload = buildModelSetupPayload(draft, canManageModelDefaultConfig, timezones)
      const updated = await updateModelAccountDescription(model.id, payload)
      onSaved?.(updated) // Display saved server values, not an optimistic unsaved draft.
      setEditing(false)
    } catch (err) {
      setError(getApiErrorMessage(err, 'Could not save setup'))
    } finally {
      setSaving(false)
    }
  }

  if (editing) {
    return (
      <Section eyebrow="Basic setup" title="Edit required model settings" description="These settings affect bot behavior, timing and operator alerts for this model.">
        <form className="v2-edit-form" onSubmit={saveSetup}>
          {error ? <div className="v2-error">{error}</div> : null}
          <label className="v2-field v2-field-wide">
            <span>Model profile</span>
            <textarea
              value={draft.description}
              onChange={(e) => updateDraft('description', e.target.value)}
              rows={6}
              aria-label="Model profile"
              aria-describedby={`profile-help-${model.id}`}
              placeholder="Persona, tone, boundaries, sales style and important context."
            />
            <small id={`profile-help-${model.id}`}>
              {profileLength(draft.description).toLocaleString('en-US')} / {MODEL_PROFILE_MAX_LENGTH.toLocaleString('en-US')} characters. Full text is saved; it is never silently shortened.
            </small>
          </label>
          <label className="v2-field">
            <span>Timezone</span>
            <input value={draft.timezone} onChange={(e) => updateDraft('timezone', e.target.value)}
              aria-label="Timezone" list={`model-timezones-${model.id}`} aria-describedby={`timezone-help-${model.id}`} placeholder="America/New_York" />
            <datalist id={`model-timezones-${model.id}`}>{timezones.map((zone) => <option key={zone} value={zone} />)}</datalist>
            <small id={`timezone-help-${model.id}`}>Choose a city-based IANA timezone. Most of Texas uses America/Chicago; El Paso uses America/Denver.</small>
          </label>
          <label className="v2-field">
            <span>Automation mode</span>
            <select value={draft.automation_mode} onChange={(e) => updateDraft('automation_mode', e.target.value)}>
              <option value="full_auto">Full auto</option>
              <option value="handoff_on_offer">Handoff on offer</option>
              <option value="handoff_after_first_purchase">Handoff after first purchase</option>
            </select>
          </label>
          <label className="v2-field">
            <span>Default graph config</span>
            {canManageModelDefaultConfig ? (
              <select value={draft.default_config_id} onChange={(e) => updateDraft('default_config_id', e.target.value)}>
                <option value="">Use active global config</option>
                {configs.map((config) => (
                  <option key={config.id} value={config.id}>{config.name || `Config #${config.id}`}{config.is_active ? ' (active)' : ''}</option>
                ))}
              </select>
            ) : (
              <input value={configDisplayName(configs, draft.default_config_id)} readOnly />
            )}
          </label>
          <label className="v2-field">
            <span>Max message price</span>
            <input
              type="number"
              min="3"
              max="200"
              value={draft.max_message_price}
              onChange={(e) => updateDraft('max_message_price', e.target.value)}
              placeholder="Default"
            />
          </label>
          <label className="v2-check-field">
            <input
              type="checkbox"
              checked={readBool(draft.bot_paused)}
              onChange={(e) => updateDraft('bot_paused', e.target.checked)}
            />
            <span>Pause bot for this model</span>
          </label>
          <label className="v2-check-field">
            <input
              type="checkbox"
              checked={draft.telegram_alerts_enabled}
              onChange={(e) => updateDraft('telegram_alerts_enabled', e.target.checked)}
            />
            <span>Telegram alerts enabled</span>
          </label>
          <div className="v2-card-actions">
            <button type="submit" className="v2-btn v2-btn-primary" disabled={saving}>{saving ? 'Saving...' : 'Save setup'}</button>
            <button type="button" className="v2-btn v2-btn-ghost" disabled={saving} onClick={() => setEditing(false)}>Cancel</button>
          </div>
        </form>
      </Section>
    )
  }

  return (
    <Section
      eyebrow="Basic vs advanced"
      title="Required model settings"
      description="Each setting explains why it exists, the recommended value and the risk of changing it."
      actions={<button type="button" className="v2-btn v2-btn-primary" onClick={() => setEditing(true)}>Edit setup</button>}
    >
      <div className="v2-settings-stack">
        <SettingExplainer
          label="Model profile"
          value={model.account_description ? 'Configured' : 'Missing'}
          description="Persona and account context used by the bot when writing messages."
          recommended="One concise paragraph with tone, boundaries and sales style."
          impact="Poor profile quality creates generic or unsafe replies."
          scope="This model only"
          risk="Low"
        />
        <SettingExplainer
          label="Timezone"
          value={model.timezone || 'Missing'}
          description="Controls proactive timing, quiet hours and local activity interpretation."
          recommended="Creator local timezone."
          impact="Wrong timezone can send messages at bad hours."
          scope="This model only"
          risk="Medium"
        />
        <SettingExplainer
          label="Automation mode"
          value={model.automation_mode || 'Missing'}
          description="Defines whether the bot can act alone or should hand off around offers."
          recommended="Handoff on offer until content and prompts are proven."
          impact="Full auto may send paid offers without manual review."
          scope="This model only"
          risk="Medium"
        />
        <SettingExplainer
          label="Default graph config"
          value={configDisplayName(configs, model.default_config_id)}
          description="Configuration used when a new conversation profile is created for this model."
          recommended="Set a model-specific config for production creators with custom persona or generator settings."
          impact="Unset models fall back to the active global configuration."
          scope="This model only"
          risk="Medium"
        />
        <SettingExplainer
          label="Bot pause"
          value={readBool(model.bot_paused) ? 'Paused' : 'Live'}
          description="Stops automatic scheduling and outbound bot sends for this model."
          recommended="Live for configured accounts; pause during incidents or account review."
          impact="Paused models will not auto-reply until resumed."
          scope="This model only"
          risk="Medium"
        />
        <SettingExplainer
          label="Telegram alerts"
          value={model.telegram_alerts_enabled ? 'Enabled' : 'Disabled'}
          description="Sends operational alerts to an external operator channel."
          recommended="Enable for live models with automation enabled."
          impact="Disabled alerts can hide blocked chats or provider failures."
          scope="This model only"
          risk="Low"
        />
      </div>
    </Section>
  )
}

function configDisplayName(configs, configId) {
  if (configId == null || configId === '') return 'Active global config'
  const match = configs.find((config) => String(config.id) === String(configId))
  return match?.name || `Config #${configId}`
}

function ConnectionsTab({ model, providers }) {
  return (
    <Section eyebrow="Provider accounts" title="Connections for this model" description="V2 is designed for multiple platforms per model.">
      <div className="v2-provider-catalog">
        {providers.length ? providers.map((link) => {
          const webhookStatus = providerWebhookStatus(link)
          return (
            <article key={link.id || link.provider} className="v2-provider-card">
              <div>
                <span className="v2-provider-logo">{providerLabel(link.provider).slice(0, 2).toUpperCase()}</span>
                <h3>{providerLabel(link.provider)}</h3>
                <p>{link.external_id || link.email || link.username || 'External account id is not shown.'}</p>
                <div className="v2-provider-card-meta">
                  <span>Webhook</span>
                  {webhookStatus ? (
                    <>
                      <StatusPill tone={statusTone(webhookStatus)}>{webhookStatus}</StatusPill>
                      <em>{providerWebhookDetail(link)}</em>
                    </>
                  ) : (
                    <em>No webhook delivery</em>
                  )}
                </div>
              </div>
              <StatusPill tone={statusTone(providerStatus(link))}>{providerStatus(link)}</StatusPill>
            </article>
          )
        }) : <EmptyState title="No providers connected" description="Connect a provider to manage platform scope here." />}
      </div>
    </Section>
  )
}

function ContentTab({
  modelId,
  model,
  folders,
  folder,
  setFolder,
  refreshMediaFolders,
  mediaLimit,
  setMediaLimit,
  showHiddenMedia,
  setShowHiddenMedia,
  mediaController,
  allowDownloads = true,
  allowAutomationSettings = true,
}) {
  const [editModalItem, setEditModalItem] = useState(null)
  const mediaSyncing = model?.media_sync_status === 'syncing'
  const syncStatusLabel = mediaSyncing
    ? 'Sync in progress'
    : model?.media_sync_status === 'done'
      ? 'Sync completed'
      : model?.media_sync_status === 'failed'
        ? 'Sync failed'
        : null

  return (
    <Section
      eyebrow="Content"
      title="Media markup, photo sessions and sale scripts"
      description="This workspace uses the same content markup flow as classic: select media, move it into a photo session or sale script, then edit step price/description or individual media metadata."
      className="v2-classic-content-section"
    >
      <section className="model-detail-media model-detail-media--full v2-classic-media-section" aria-label="Media">
        <ModelDetailMediaSection
          modelId={modelId}
          model={model}
          mediaSyncing={mediaSyncing}
          syncStatusLabel={syncStatusLabel}
          mediaFolders={folders}
          mediaFolder={folder}
          setMediaFolder={setFolder}
          refreshMediaFolders={refreshMediaFolders}
          mediaLimit={mediaLimit}
          setMediaLimit={setMediaLimit}
          showHiddenMedia={showHiddenMedia}
          setShowHiddenMedia={setShowHiddenMedia}
          highQualityThumbnails={false}
          editModalItem={editModalItem}
          setEditModalItem={setEditModalItem}
          {...mediaController}
          allowDownloads={allowDownloads}
          allowAutomationSettings={allowAutomationSettings}
        />
      </section>
    </Section>
  )
}

function V2SessionBuilder({ modelId, sessions, sessionDetails = [], loadingSessionDetails = false, selectedMediaIds, onClearSelection, onChanged, onEditMedia }) {
  const [sessionName, setSessionName] = useState('')
  const [sessionDescription, setSessionDescription] = useState('')
  const [sessionTags, setSessionTags] = useState('')
  const [sessionKind, setSessionKind] = useState('photoset')
  const [targetSessionId, setTargetSessionId] = useState('')
  const [targetGroupId, setTargetGroupId] = useState('')
  const [editingSessionName, setEditingSessionName] = useState('')
  const [editingSessionDescription, setEditingSessionDescription] = useState('')
  const [editingSessionTags, setEditingSessionTags] = useState('')
  const [stageName, setStageName] = useState('')
  const [priceMin, setPriceMin] = useState('')
  const [priceMax, setPriceMax] = useState('')
  const [busy, setBusy] = useState('')
  const [message, setMessage] = useState('')

  const targetSession = sessions.find((session) => String(session.id) === String(targetSessionId))
  const groups = targetSession?.media_groups || []

  function parseTags(value) {
    return String(value || '')
      .split(',')
      .map((tag) => tag.trim())
      .filter(Boolean)
  }

  useEffect(() => {
    if (!targetSession) {
      setEditingSessionName('')
      setEditingSessionDescription('')
      setEditingSessionTags('')
      return
    }
    setEditingSessionName(targetSession.name || '')
    setEditingSessionDescription(targetSession.description || '')
    setEditingSessionTags(Array.isArray(targetSession.selection_tags) ? targetSession.selection_tags.join(', ') : '')
  }, [targetSession?.id, targetSession?.name, targetSession?.description, targetSession?.selection_tags])

  async function createSession(event) {
    event.preventDefault()
    const name = sessionName.trim()
    if (!name) {
      setMessage('Enter a session name.')
      return
    }
    setBusy('session')
    setMessage('')
    try {
      const created = await createPhotoSession(modelId, name, sessionKind === 'script', {
        description: sessionDescription.trim(),
        selection_tags: parseTags(sessionTags),
      })
      setSessionName('')
      setSessionDescription('')
      setSessionTags('')
      setTargetSessionId(String(created.id))
      setMessage(`${created.is_script ? 'Sale script' : 'Photo session'} created.`)
      onChanged?.()
    } catch (err) {
      setMessage(getApiErrorMessage(err, 'Failed to create session'))
    } finally {
      setBusy('')
    }
  }

  async function updateSessionMeta(event) {
    event.preventDefault()
    if (!targetSession) {
      setMessage('Choose a session first.')
      return
    }
    const name = editingSessionName.trim() || targetSession.name || `Session #${targetSession.id}`
    setBusy('session-meta')
    setMessage('')
    try {
      await updatePhotoSession(modelId, targetSession.id, {
        name,
        description: editingSessionDescription.trim(),
        selection_tags: parseTags(editingSessionTags),
        is_script: targetSession.is_script,
        time_tag: targetSession.time_tag || '',
      })
      setMessage('Session details saved.')
      onChanged?.()
    } catch (err) {
      setMessage(getApiErrorMessage(err, 'Failed to save session details'))
    } finally {
      setBusy('')
    }
  }

  async function createStage(event) {
    event.preventDefault()
    if (!targetSessionId) {
      setMessage('Choose a session first.')
      return
    }
    setBusy('stage')
    setMessage('')
    try {
      const group = await createMediaGroup(modelId, targetSessionId, stageName.trim())
      const minValue = priceMin === '' ? null : Number(priceMin)
      const maxValue = priceMax === '' ? null : Number(priceMax)
      if (priceMin !== '' || priceMax !== '') {
        await updateMediaGroup(modelId, targetSessionId, group.id, {
          name: stageName.trim(),
          price_min: Number.isFinite(minValue) ? minValue : null,
          price_max: Number.isFinite(maxValue) ? maxValue : null,
        })
      }
      setStageName('')
      setPriceMin('')
      setPriceMax('')
      setTargetGroupId(String(group.id))
      setMessage('Stage created.')
      onChanged?.()
    } catch (err) {
      setMessage(getApiErrorMessage(err, 'Failed to create stage'))
    } finally {
      setBusy('')
    }
  }

  async function moveSelected() {
    if (!selectedMediaIds.length) {
      setMessage('Select media first.')
      return
    }
    if (!targetSessionId) {
      setMessage('Choose a target session.')
      return
    }
    setBusy('move')
    setMessage('')
    try {
      if (targetGroupId) {
        await addMediaToGroup(modelId, targetSessionId, targetGroupId, selectedMediaIds)
      } else {
        await moveMediaToSession(modelId, selectedMediaIds, Number(targetSessionId))
      }
      setMessage(`${selectedMediaIds.length} media moved.`)
      onClearSelection?.()
      onChanged?.()
    } catch (err) {
      setMessage(getApiErrorMessage(err, 'Failed to move selected media'))
    } finally {
      setBusy('')
    }
  }

  return (
    <div className="v2-session-builder">
      <div className="v2-session-builder-header">
        <div>
          <p className="v2-kicker">Photo sessions and sale scripts</p>
          <h3>Build structured content</h3>
        </div>
        <StatusPill tone={selectedMediaIds.length ? 'good' : 'neutral'}>{selectedMediaIds.length} selected</StatusPill>
      </div>
      {message ? <div className="v2-info">{message}</div> : null}
      <div className="v2-session-builder-grid">
        <form className="v2-session-card" onSubmit={createSession}>
          <h4>Create session</h4>
          <label className="v2-field">
            <span>Name</span>
            <input value={sessionName} onChange={(event) => setSessionName(event.target.value)} placeholder="Morning teaser set" />
          </label>
          <label className="v2-field">
            <span>Script description</span>
            <textarea value={sessionDescription} onChange={(event) => setSessionDescription(event.target.value)} rows={3} placeholder="When this script fits: vibe, outfit, setting, sales angle." />
          </label>
          <label className="v2-field">
            <span>Selection tags</span>
            <input value={sessionTags} onChange={(event) => setSessionTags(event.target.value)} placeholder="red dress, garden, soft tease" />
          </label>
          <label className="v2-field">
            <span>Type</span>
            <select value={sessionKind} onChange={(event) => setSessionKind(event.target.value)}>
              <option value="photoset">Photo session</option>
              <option value="script">Sale script</option>
            </select>
          </label>
          <button type="submit" className="v2-btn v2-btn-primary" disabled={!!busy}>{busy === 'session' ? 'Creating...' : 'Create'}</button>
        </form>

        <form className="v2-session-card" onSubmit={updateSessionMeta}>
          <h4>Edit session details</h4>
          <label className="v2-field">
            <span>Session</span>
            <select value={targetSessionId} onChange={(event) => { setTargetSessionId(event.target.value); setTargetGroupId('') }}>
              <option value="">Choose session</option>
              {sessions.map((session) => (
                <option key={session.id} value={session.id}>{session.name}{session.is_script ? ' - script' : ''}</option>
              ))}
            </select>
          </label>
          <label className="v2-field">
            <span>Name</span>
            <input value={editingSessionName} onChange={(event) => setEditingSessionName(event.target.value)} disabled={!targetSession} />
          </label>
          <label className="v2-field">
            <span>Script description</span>
            <textarea value={editingSessionDescription} onChange={(event) => setEditingSessionDescription(event.target.value)} rows={3} placeholder="Used by content selection before step/media descriptions." disabled={!targetSession} />
          </label>
          <label className="v2-field">
            <span>Selection tags</span>
            <input value={editingSessionTags} onChange={(event) => setEditingSessionTags(event.target.value)} placeholder="feet, bedroom, cosplay, video" disabled={!targetSession} />
          </label>
          <button type="submit" className="v2-btn v2-btn-secondary" disabled={!!busy || !targetSession}>
            {busy === 'session-meta' ? 'Saving...' : 'Save details'}
          </button>
        </form>

        <form className="v2-session-card" onSubmit={createStage}>
          <h4>Create stage</h4>
          <label className="v2-field">
            <span>Session</span>
            <select value={targetSessionId} onChange={(event) => { setTargetSessionId(event.target.value); setTargetGroupId('') }}>
              <option value="">Choose session</option>
              {sessions.map((session) => (
                <option key={session.id} value={session.id}>{session.name}{session.is_script ? ' - script' : ''}</option>
              ))}
            </select>
          </label>
          <label className="v2-field">
            <span>Stage name</span>
            <input value={stageName} onChange={(event) => setStageName(event.target.value)} placeholder="Stage 1" />
          </label>
          <div className="v2-price-row">
            <label className="v2-field">
              <span>Min $</span>
              <input type="number" min="0" max="200" value={priceMin} onChange={(event) => setPriceMin(event.target.value)} />
            </label>
            <label className="v2-field">
              <span>Max $</span>
              <input type="number" min="0" max="200" value={priceMax} onChange={(event) => setPriceMax(event.target.value)} />
            </label>
          </div>
          <button type="submit" className="v2-btn v2-btn-secondary" disabled={!!busy || !targetSessionId}>{busy === 'stage' ? 'Creating...' : 'Create stage'}</button>
        </form>

        <div className="v2-session-card">
          <h4>Move selected media</h4>
          <label className="v2-field">
            <span>Session</span>
            <select value={targetSessionId} onChange={(event) => { setTargetSessionId(event.target.value); setTargetGroupId('') }}>
              <option value="">Choose session</option>
              {sessions.map((session) => (
                <option key={session.id} value={session.id}>{session.name}{session.is_script ? ' - script' : ''}</option>
              ))}
            </select>
          </label>
          <label className="v2-field">
            <span>Stage or ungrouped</span>
            <select value={targetGroupId} onChange={(event) => setTargetGroupId(event.target.value)} disabled={!targetSessionId}>
              <option value="">Ungrouped in session</option>
              {groups.map((group) => (
                <option key={group.id} value={group.id}>{group.name || `Stage #${group.sort_order + 1}`}</option>
              ))}
            </select>
          </label>
          <button type="button" className="v2-btn v2-btn-primary" disabled={!!busy || !selectedMediaIds.length || !targetSessionId} onClick={moveSelected}>
            {busy === 'move' ? 'Moving...' : 'Move selected'}
          </button>
        </div>
      </div>
      <V2ScriptVisualBoard
        modelId={modelId}
        sessions={sessionDetails.length ? sessionDetails : sessions}
        loading={loadingSessionDetails}
        onChanged={onChanged}
        onEditMedia={onEditMedia}
        setMessage={setMessage}
      />
    </div>
  )
}

function V2ScriptVisualBoard({ modelId, sessions, loading, onChanged, onEditMedia, setMessage }) {
  const [dragMedia, setDragMedia] = useState(null)
  const [mediaDrop, setMediaDrop] = useState(null)
  const [dragStage, setDragStage] = useState(null)
  const [stageDrop, setStageDrop] = useState(null)
  const [saving, setSaving] = useState('')

  const sortedSessions = useMemo(
    () => [...asList(sessions)].sort((a, b) => (a.sort_order ?? 999999) - (b.sort_order ?? 999999) || (a.id ?? 0) - (b.id ?? 0)),
    [sessions],
  )
  const scriptSessions = sortedSessions.filter((session) => session.is_script)
  const photoSessions = sortedSessions.filter((session) => !session.is_script)

  function sortedGroups(session) {
    return asList(session.media_groups)
      .filter((group) => group.id != null || asList(group.media).length)
      .sort((a, b) => (a.sort_order ?? 999999) - (b.sort_order ?? 999999) || (a.id ?? 0) - (b.id ?? 0))
  }

  function groupPriceLabel(group) {
    if (group?.price_min == null && group?.price_max == null) return 'No step price'
    const min = group.price_min ?? group.price_max ?? 0
    const max = group.price_max ?? group.price_min ?? 0
    return min === max ? `$${max}` : `$${min}-${max}`
  }

  function mediaPriceLabel(item) {
    if (item?.price_min == null && item?.price_max == null) return ''
    const min = item.price_min ?? item.price_max ?? 0
    const max = item.price_max ?? item.price_min ?? 0
    return min === max ? `$${max}` : `$${min}-${max}`
  }

  function dropIndexFromEvent(event, index) {
    const rect = event.currentTarget.getBoundingClientRect()
    return event.clientX > rect.left + rect.width / 2 ? index + 1 : index
  }

  async function handleMediaDrop(session, group, dropIndex) {
    const source = dragMedia
    setDragMedia(null)
    setMediaDrop(null)
    if (!source || source.sessionId !== session.id || source.groupId !== group.id) return
    const mediaRows = asList(group.media)
    const fromIndex = source.fromIndex
    if (dropIndex === fromIndex || dropIndex === fromIndex + 1) return
    const next = [...mediaRows]
    const [removed] = next.splice(fromIndex, 1)
    const adjusted = dropIndex > fromIndex ? dropIndex - 1 : dropIndex
    next.splice(adjusted, 0, removed)
    const ids = next.map((item) => item.id).filter(Boolean)
    if (!ids.length) return
    setSaving(`media:${session.id}:${group.id ?? 'ungrouped'}`)
    try {
      if (group.id == null) await reorderPhotoSessionMedia(modelId, session.id, ids)
      else await reorderGroupMedia(modelId, session.id, group.id, ids)
      setMessage?.('Media order saved.')
      onChanged?.()
    } catch (err) {
      setMessage?.(getApiErrorMessage(err, 'Failed to reorder media'))
    } finally {
      setSaving('')
    }
  }

  async function handleRemoveMediaFromSession(session, item) {
    if (!session?.id || !item?.id) return
    const label = session.is_script ? 'script' : 'session'
    const ok = window.confirm(`Remove this media from the ${label}? The file will stay in the media library.`)
    if (!ok) return
    setSaving(`remove:${session.id}:${item.id}`)
    try {
      await removeMediaFromPhotoSession(modelId, session.id, item.id)
      setMessage?.(`Media removed from ${label}. File stays in library.`)
      onChanged?.()
    } catch (err) {
      setMessage?.(getApiErrorMessage(err, `Failed to remove media from ${label}`))
    } finally {
      setSaving('')
    }
  }

  async function handleStageDrop(session, dropIndex) {
    const source = dragStage
    setDragStage(null)
    setStageDrop(null)
    if (!source || source.sessionId !== session.id) return
    if (source.fromIndex === dropIndex || source.fromIndex === dropIndex - 1) return
    const groups = sortedGroups(session).filter((group) => group.id != null)
    const next = [...groups]
    const [removed] = next.splice(source.fromIndex, 1)
    const adjusted = dropIndex > source.fromIndex ? dropIndex - 1 : dropIndex
    next.splice(adjusted, 0, removed)
    const ids = next.map((group) => group.id).filter(Boolean)
    if (!ids.length) return
    setSaving(`stage:${session.id}`)
    try {
      await reorderMediaGroups(modelId, session.id, ids)
      setMessage?.('Step order saved.')
      onChanged?.()
    } catch (err) {
      setMessage?.(getApiErrorMessage(err, 'Failed to reorder steps'))
    } finally {
      setSaving('')
    }
  }

  function renderLane(title, description, rows, emptyTitle) {
    return (
      <section className="v2-script-lane">
        <div className="v2-script-lane-heading">
          <div>
            <h4>{title}</h4>
            <p>{description}</p>
          </div>
          <StatusPill tone={rows.length ? 'good' : 'neutral'}>{rows.length}</StatusPill>
        </div>
        {rows.length ? rows.map((session) => (
          <V2SessionVisualCard
            key={session.id}
            modelId={modelId}
            session={session}
            groups={sortedGroups(session)}
            saving={saving}
            dragMedia={dragMedia}
            mediaDrop={mediaDrop}
            dragStage={dragStage}
            stageDrop={stageDrop}
            setDragMedia={setDragMedia}
            setMediaDrop={setMediaDrop}
            setDragStage={setDragStage}
            setStageDrop={setStageDrop}
            onMediaDrop={handleMediaDrop}
            onStageDrop={handleStageDrop}
            onChanged={onChanged}
            onEditMedia={onEditMedia}
            onRemoveMediaFromSession={handleRemoveMediaFromSession}
            setMessage={setMessage}
            groupPriceLabel={groupPriceLabel}
            mediaPriceLabel={mediaPriceLabel}
            dropIndexFromEvent={dropIndexFromEvent}
          />
        )) : <EmptyState title={emptyTitle} description="Create one above, then move selected media into a step." />}
      </section>
    )
  }

  return (
    <div className="v2-script-board">
      <div className="v2-script-board-heading">
        <div>
          <p className="v2-kicker">Visual builder</p>
          <h3>Scripts, steps and media</h3>
          <p>Step price/description lives on the stage. Individual media price/description lives on each media item.</p>
        </div>
        {loading ? <StatusPill tone="warn">Loading media</StatusPill> : null}
      </div>
      <div className="v2-script-board-grid">
        {renderLane('Sale scripts', 'Ordered stages used by the sales flow. Drag stages or media inside a stage to reorder.', scriptSessions, 'No sale scripts yet')}
        {renderLane('Photo sessions', 'Reusable content sets for chat media and non-script organization.', photoSessions, 'No photo sessions yet')}
      </div>
    </div>
  )
}

function V2SessionVisualCard({
  modelId,
  session,
  groups,
  saving,
  dragMedia,
  mediaDrop,
  dragStage,
  stageDrop,
  setDragMedia,
  setMediaDrop,
  setDragStage,
  setStageDrop,
  onMediaDrop,
  onStageDrop,
  onChanged,
  onEditMedia,
  onRemoveMediaFromSession,
  setMessage,
  groupPriceLabel,
  mediaPriceLabel,
  dropIndexFromEvent,
}) {
  const reorderableGroups = groups.filter((group) => group.id != null)
  return (
    <article className="v2-script-session">
      <header className="v2-script-session-header">
        <div>
          <strong>{session.name || `Session #${session.id}`}</strong>
          <span>{session.is_script ? 'Sale script' : 'Photo session'} · {session.media_count || asList(session.media).length || 0} media</span>
          {session.description ? <p className="v2-script-session-description">{session.description}</p> : null}
          {Array.isArray(session.selection_tags) && session.selection_tags.length ? (
            <div className="v2-script-session-tags">
              {session.selection_tags.slice(0, 8).map((tag) => (
                <em key={tag}>{tag}</em>
              ))}
            </div>
          ) : null}
        </div>
        <StatusPill tone={session.is_active ? 'good' : 'neutral'}>{session.time_tag || 'Any time'}</StatusPill>
      </header>
      <div className="v2-stage-stack">
        {groups.length ? groups.map((group, index) => {
          const reorderIndex = group.id == null ? -1 : reorderableGroups.findIndex((item) => item.id === group.id)
          const canDragStage = reorderIndex >= 0
          const showBefore = canDragStage && stageDrop?.sessionId === session.id && stageDrop?.dropIndex === reorderIndex
          const showAfter = canDragStage && reorderIndex === reorderableGroups.length - 1 && stageDrop?.sessionId === session.id && stageDrop?.dropIndex === reorderableGroups.length
          return (
            <React.Fragment key={group.id ?? `ungrouped-${session.id}`}>
              {showBefore ? <div className="v2-stage-drop-marker" /> : null}
              <V2StageCard
                modelId={modelId}
                session={session}
                group={group}
                index={index}
                reorderIndex={reorderIndex}
                canDragStage={canDragStage}
                saving={saving}
            dragMedia={dragMedia}
            mediaDrop={mediaDrop}
            dragStage={dragStage}
            stageDrop={stageDrop}
            setDragMedia={setDragMedia}
                setMediaDrop={setMediaDrop}
                setDragStage={setDragStage}
                setStageDrop={setStageDrop}
                onMediaDrop={onMediaDrop}
                onStageDrop={onStageDrop}
                onChanged={onChanged}
                onEditMedia={onEditMedia}
                onRemoveMediaFromSession={onRemoveMediaFromSession}
                setMessage={setMessage}
                groupPriceLabel={groupPriceLabel}
                mediaPriceLabel={mediaPriceLabel}
                dropIndexFromEvent={dropIndexFromEvent}
              />
              {showAfter ? <div className="v2-stage-drop-marker" /> : null}
            </React.Fragment>
          )
        }) : (
          <div className="v2-stage-empty">No stages yet. Create a stage above, then move selected media into it.</div>
        )}
      </div>
    </article>
  )
}

function V2StageCard({
  modelId,
  session,
  group,
  index,
  reorderIndex,
  canDragStage,
  saving,
  dragMedia,
  mediaDrop,
  dragStage,
  stageDrop,
  setDragMedia,
  setMediaDrop,
  setDragStage,
  setStageDrop,
  onMediaDrop,
  onStageDrop,
  onChanged,
  onEditMedia,
  onRemoveMediaFromSession,
  setMessage,
  groupPriceLabel,
  mediaPriceLabel,
  dropIndexFromEvent,
}) {
  const [expanded, setExpanded] = useState(false)
  const [draft, setDraft] = useState({
    name: group.name || '',
    description: group.description || '',
    price_min: group.price_min == null ? '' : String(group.price_min),
    price_max: group.price_max == null ? '' : String(group.price_max),
  })
  const mediaRows = asList(group.media)
  const isSaving = saving === `media:${session.id}:${group.id ?? 'ungrouped'}` || saving === `stage:${session.id}`
  const activeMediaDrag = dragMedia?.sessionId === session.id && dragMedia?.groupId === group.id
  const activeStageDrag = dragStage?.sessionId === session.id && dragStage?.groupId === group.id

  useEffect(() => {
    setDraft({
      name: group.name || '',
      description: group.description || '',
      price_min: group.price_min == null ? '' : String(group.price_min),
      price_max: group.price_max == null ? '' : String(group.price_max),
    })
  }, [group.id, group.name, group.description, group.price_min, group.price_max])

  async function saveStep(event) {
    event.preventDefault()
    if (!group.id) return
    try {
      await updateMediaGroup(modelId, session.id, group.id, {
        name: draft.name.trim() || null,
        description: draft.description.trim() || null,
        price_min: draft.price_min === '' ? null : Number(draft.price_min),
        price_max: draft.price_max === '' ? null : Number(draft.price_max),
      })
      setMessage?.('Step saved.')
      setExpanded(false)
      onChanged?.()
    } catch (err) {
      setMessage?.(getApiErrorMessage(err, 'Failed to save step'))
    }
  }

  return (
    <article
      className={`v2-stage-card ${activeStageDrag ? 'is-dragging' : ''}`.trim()}
      onDragOver={canDragStage ? (event) => {
        if (!dragStage || dragStage.sessionId !== session.id) return
        event.preventDefault()
        const rect = event.currentTarget.getBoundingClientRect()
        const dropIndex = event.clientY > rect.top + rect.height / 2 ? reorderIndex + 1 : reorderIndex
        setStageDrop({ sessionId: session.id, dropIndex })
      } : undefined}
      onDrop={canDragStage ? (event) => { event.preventDefault(); onStageDrop(session, stageDrop?.dropIndex ?? reorderIndex) } : undefined}
    >
      <header className="v2-stage-card-header">
        <div className="v2-stage-title-row">
          {canDragStage ? (
            <button
              type="button"
              className="v2-drag-handle"
              draggable
              onDragStart={(event) => {
                event.dataTransfer.effectAllowed = 'move'
                setDragStage({ sessionId: session.id, groupId: group.id, fromIndex: reorderIndex })
              }}
              onDragEnd={() => {
                setDragStage(null)
                setStageDrop(null)
              }}
              title="Drag step"
              aria-label="Drag step"
            >
              <span />
            </button>
          ) : null}
          <div>
            <strong>{group.id == null ? 'Ungrouped media' : (group.name || `Step ${index + 1}`)}</strong>
            <span>{group.description || 'No step description yet'}</span>
          </div>
        </div>
        <div className="v2-stage-meta">
          <StatusPill tone={group.price_min != null || group.price_max != null ? 'good' : 'warn'}>{groupPriceLabel(group)}</StatusPill>
          <StatusPill tone={mediaRows.length ? 'good' : 'neutral'}>{mediaRows.length} media</StatusPill>
          {group.id ? (
            <button type="button" className="v2-mini-button" onClick={() => setExpanded((value) => !value)}>
              {expanded ? 'Close' : 'Edit step'}
            </button>
          ) : null}
        </div>
      </header>

      {expanded && group.id ? (
        <form className="v2-stage-edit-form" onSubmit={saveStep}>
          <label className="v2-field">
            <span>Step name</span>
            <input value={draft.name} onChange={(event) => setDraft((prev) => ({ ...prev, name: event.target.value }))} placeholder="Stage 1" />
          </label>
          <label className="v2-field v2-field-wide">
            <span>Step description</span>
            <textarea value={draft.description} onChange={(event) => setDraft((prev) => ({ ...prev, description: event.target.value }))} rows={3} placeholder="Description for this whole step." />
          </label>
          <div className="v2-price-row">
            <label className="v2-field">
              <span>Step min $</span>
              <input type="number" min="0" max="200" value={draft.price_min} onChange={(event) => setDraft((prev) => ({ ...prev, price_min: event.target.value }))} />
            </label>
            <label className="v2-field">
              <span>Step max $</span>
              <input type="number" min="0" max="200" value={draft.price_max} onChange={(event) => setDraft((prev) => ({ ...prev, price_max: event.target.value }))} />
            </label>
          </div>
          <button type="submit" className="v2-btn v2-btn-secondary">Save step</button>
        </form>
      ) : null}

      <div
        className={`v2-stage-media-strip ${activeMediaDrag ? 'is-drop-active' : ''}`.trim()}
        onDragOver={(event) => {
          if (!dragMedia || dragMedia.sessionId !== session.id || dragMedia.groupId !== group.id) return
          event.preventDefault()
          setMediaDrop({ sessionId: session.id, groupId: group.id, dropIndex: mediaRows.length })
        }}
        onDrop={(event) => {
          if (!dragMedia || dragMedia.sessionId !== session.id || dragMedia.groupId !== group.id) return
          event.preventDefault()
          onMediaDrop(session, group, mediaDrop?.dropIndex ?? mediaRows.length)
        }}
      >
        {mediaRows.length ? mediaRows.map((item, mediaIndex) => (
          <V2StageMediaThumb
            key={item.id}
            modelId={modelId}
            item={item}
            mediaIndex={mediaIndex}
            mediaPriceLabel={mediaPriceLabel}
            isSaving={isSaving}
            isRemoving={saving === `remove:${session.id}:${item.id}`}
            isDropBefore={activeMediaDrag && mediaDrop?.dropIndex === mediaIndex}
            isDragging={activeMediaDrag && dragMedia?.fromIndex === mediaIndex}
            onEditMedia={onEditMedia}
            onRemoveFromSession={() => onRemoveMediaFromSession?.(session, item)}
            onDragStart={(event) => {
              event.dataTransfer.effectAllowed = 'move'
              setDragMedia({ sessionId: session.id, groupId: group.id, fromIndex: mediaIndex })
            }}
            onDragOver={(event) => {
              if (!dragMedia || dragMedia.sessionId !== session.id || dragMedia.groupId !== group.id) return
              event.preventDefault()
              setMediaDrop({ sessionId: session.id, groupId: group.id, dropIndex: dropIndexFromEvent(event, mediaIndex) })
            }}
            onDrop={(event) => {
              if (!dragMedia || dragMedia.sessionId !== session.id || dragMedia.groupId !== group.id) return
              event.preventDefault()
              onMediaDrop(session, group, mediaDrop?.dropIndex ?? mediaIndex)
            }}
            onDragEnd={() => {
              setDragMedia(null)
              setMediaDrop(null)
            }}
          />
        )) : (
          <div className="v2-stage-empty">No media in this step.</div>
        )}
      </div>
    </article>
  )
}

function V2StageMediaThumb({ modelId, item, mediaIndex, mediaPriceLabel, isSaving, isRemoving = false, isDropBefore, isDragging, onEditMedia, onRemoveFromSession, onDragStart, onDragOver, onDrop, onDragEnd }) {
  const preview = getMediaPreview(item)
  const title = item.description || item.external_id || `Media #${item.id}`
  const price = mediaPriceLabel(item)
  return (
    <article
      className={`v2-stage-media-thumb ${isDropBefore ? 'is-drop-before' : ''} ${isDragging ? 'is-dragging' : ''}`.trim()}
      draggable={!isSaving}
      onDragStart={onDragStart}
      onDragOver={onDragOver}
      onDrop={onDrop}
      onDragEnd={onDragEnd}
      title={title}
    >
      <div className="v2-stage-media-image">
        {preview ? (
          preview.local ? (
            <MediaThumbnail
              modelId={modelId}
              mediaId={preview.mediaId}
              kind="thumb"
              fallbackKind="preview"
              className="v2-media-thumb-image"
              alt={title}
            />
          ) : (
            <MediaThumbnail modelId={modelId} cdnUrl={preview.cdnUrl} className="v2-media-thumb-image" alt={title} />
          )
        ) : (
          <span>{item.type || item.media_type || 'media'}</span>
        )}
        <em>{mediaIndex + 1}</em>
      </div>
      <div className="v2-stage-media-caption">
        <strong>{title}</strong>
        <span>{item.is_paid ? (price || 'Paid, no media price') : 'Free media'}</span>
      </div>
      <div className="v2-stage-media-actions">
        <button type="button" className="v2-mini-button" onClick={() => onEditMedia?.(item)}>Edit media</button>
        <button type="button" className="v2-mini-button v2-mini-button-danger" disabled={isSaving || isRemoving} onClick={onRemoveFromSession}>
          {isRemoving ? 'Removing...' : 'Remove'}
        </button>
      </div>
    </article>
  )
}

function AutomationTab({ model }) {
  return (
    <div className="v2-stack">
      <Section eyebrow="Automation" title="Safe controls first, advanced internals second" description="Operators see explainable controls; graph internals remain available as advanced tools.">
        <div className="v2-settings-stack">
          <SettingExplainer
            label="Automation mode"
            value={model.automation_mode || 'Missing'}
            description="Controls how much the bot can do without operator approval."
            recommended="Handoff on offer for most live models."
            impact="Changes active conversation behavior."
            scope="This model only"
            risk="Medium"
          />
          <SettingExplainer
            label="Bot pause"
            value={readBool(model.bot_paused) ? 'Paused' : 'Live'}
            description="Stops automatic scheduling and outbound bot sends for this model."
            recommended="Live unless the account is under review or a provider incident is active."
            impact="Paused models will not auto-reply until resumed."
            scope="This model only"
            risk="Medium"
          />
          <SettingExplainer
            label="Max message price"
            value={model.max_message_price != null ? String(model.max_message_price) : 'Default'}
            description="Caps paid message offers created for this model."
            recommended="Use a model-specific cap only when strategy requires it."
            impact="Can reduce revenue or prevent too-expensive offers."
            scope="This model only"
            risk="Medium"
          />
        </div>
        <div className="v2-advanced-links">
          <span>Configurations</span>
          <span>Graph</span>
          <span>Agents</span>
          <span>Proactive</span>
        </div>
      </Section>
      <TrafficSegmentationPanel modelId={model.id} />
    </div>
  )
}

function readBool(value) {
  if (typeof value === 'boolean') return value
  if (typeof value === 'number') return value !== 0
  if (typeof value === 'string') return !['', '0', 'false', 'no', 'off'].includes(value.trim().toLowerCase())
  return Boolean(value)
}

function ActivityTab({ model, providers }) {
  return (
    <Section eyebrow="Activity" title="Operational timeline" description="This V2 view groups provider and media events into one readable timeline.">
      <div className="v2-timeline">
        <TimelineItem title="Model workspace opened" detail={`Current model data loaded for ${modelTitle(model)}.`} time="Now" />
        <TimelineItem title="Media sync status" detail={model.media_sync_status || 'No media sync status available.'} time={formatDate(model.media_sync_updated_at)} />
        {providers.map((link) => (
          <TimelineItem key={link.id || link.provider} title={`${providerLabel(link.provider)} connection`} detail={providerStatus(link)} time={formatDate(link.updated_at || link.created_at)} />
        ))}
      </div>
    </Section>
  )
}

function TimelineItem({ title, detail, time }) {
  return (
    <article className="v2-timeline-item">
      <span />
      <div>
        <strong>{title}</strong>
        <p>{detail}</p>
      </div>
      <em>{time}</em>
    </article>
  )
}
